import { useState, useEffect } from 'react';
import { buyerListService } from '@/services/buyerListService';
import { BuyerList } from '@/types/buyerSearch';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

export const useBuyerLists = () => {
  const [lists, setLists] = useState<BuyerList[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchLists = async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await buyerListService.getSellerLists(user.id);
      
      if (error) {
        toast({
          title: "Error",
          description: error,
          variant: "destructive"
        });
        return;
      }

      setLists(data);
    } catch (error) {
      console.error('Error fetching buyer lists:', error);
      toast({
        title: "Error",
        description: "Failed to fetch buyer lists",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const createList = async (listName: string, description?: string) => {
    if (!user?.id) return { success: false };

    try {
      const { data, error } = await buyerListService.createList(user.id, listName, description);
      
      if (error) {
        toast({
          title: "Error",
          description: error,
          variant: "destructive"
        });
        return { success: false };
      }

      toast({
        title: "Success",
        description: "Buyer list created successfully"
      });

      await fetchLists(); // Refresh the list
      return { success: true, data };
    } catch (error) {
      console.error('Error creating buyer list:', error);
      toast({
        title: "Error",
        description: "Failed to create buyer list",
        variant: "destructive"
      });
      return { success: false };
    }
  };

  const updateList = async (listId: string, updates: Partial<BuyerList>) => {
    try {
      const { success, error } = await buyerListService.updateList(listId, updates);
      
      if (!success) {
        toast({
          title: "Error",
          description: error,
          variant: "destructive"
        });
        return false;
      }

      toast({
        title: "Success",
        description: "Buyer list updated successfully"
      });

      await fetchLists(); // Refresh the list
      return true;
    } catch (error) {
      console.error('Error updating buyer list:', error);
      toast({
        title: "Error",
        description: "Failed to update buyer list",
        variant: "destructive"
      });
      return false;
    }
  };

  const deleteList = async (listId: string) => {
    try {
      const { success, error } = await buyerListService.deleteList(listId);
      
      if (!success) {
        toast({
          title: "Error",
          description: error,
          variant: "destructive"
        });
        return false;
      }

      toast({
        title: "Success",
        description: "Buyer list deleted successfully"
      });

      await fetchLists(); // Refresh the list
      return true;
    } catch (error) {
      console.error('Error deleting buyer list:', error);
      toast({
        title: "Error",
        description: "Failed to delete buyer list",
        variant: "destructive"
      });
      return false;
    }
  };

  const addBuyerToList = async (listId: string, buyerId: string, notes?: string) => {
    try {
      const { success, error } = await buyerListService.addBuyerToList(listId, buyerId, notes);
      
      if (!success) {
        toast({
          title: "Error",
          description: error,
          variant: "destructive"
        });
        return false;
      }

      toast({
        title: "Success",
        description: "Buyer added to list successfully"
      });

      await fetchLists(); // Refresh to update counts
      return true;
    } catch (error) {
      console.error('Error adding buyer to list:', error);
      toast({
        title: "Error",
        description: "Failed to add buyer to list",
        variant: "destructive"
      });
      return false;
    }
  };

  // Initial load
  useEffect(() => {
    fetchLists();
  }, [user?.id]);

  return {
    lists,
    isLoading,
    fetchLists,
    createList,
    updateList,
    deleteList,
    addBuyerToList
  };
};